/**
 * INVESTOR PORTAL. The same verified identity in "investor mode" (super-app), no separate login.
 *
 * Portfolio value, what it is made of, what it is earning. Read-only: this reports on investments,
 * it does not place them. Placing them happens in the Marketplace and settles through the compliance
 * spine.
 */

import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { StatTile as Stat } from '@/components/dash';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';
import { Reveal, Stagger, StaggerItem } from '@/components/motion';

const BAR: Record<string, string> = {
  emerald: 'bg-emerald-500',
  lime: 'bg-lime-500',
  amber: 'bg-amber-500',
  violet: 'bg-violet-500',
  sky: 'bg-sky-500',
  teal: 'bg-teal-500',
};

export default function Portfolio() {
  const { data, loading, error, reload } = useResource(() => api.portfolio(), []);

  if (loading)
    return (
      <div className="space-y-6">
        <LoadingCard lines={3} />
        <LoadingCard lines={4} />
      </div>
    );
  if (error) return <ErrorState body="We could not load your portfolio." onRetry={reload} />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-page-title text-ink">Portfolio</h1>
          <p className="mt-0.5 text-sm text-ink-muted">Your investments across tokenized assets.</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Link
            to="/portfolio/analytics"
            className="rounded-field border border-border bg-surface px-3 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            Analytics
          </Link>
          <Link
            to="/portfolio/tax"
            className="rounded-field border border-border bg-surface px-3 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            Tax
          </Link>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
        {/* MAIN */}
        <div className="space-y-6 lg:col-span-2">
          {/* VALUE HERO */}
          <Reveal>
            <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary to-primary-dark p-5 text-white shadow-lifted">
              <div
                aria-hidden
                className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-primary-light/40 blur-2xl"
              />
              <div className="relative">
                <p className="text-sm text-white/70">Portfolio value</p>
                <p className="mt-1 flex items-baseline gap-2">
                  <span className="text-sm font-medium text-white/70">KES</span>
                  <span className="tabular text-4xl font-semibold tracking-tight">
                    {data.value}
                  </span>
                </p>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  <span className="inline-flex items-center gap-1 rounded-pill bg-white/15 px-2.5 py-0.5 font-medium text-white">
                    &uarr; {data.returnsPct}% total return
                  </span>
                  <span className="text-white/75">Invested KES {data.invested}</span>
                </div>
              </div>
            </section>
          </Reveal>

          {/* STAT ROW */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Income to date" value={`KES ${data.incomeToDate}`} accent />
            <Stat label="Projected / yr" value={`KES ${data.projectedAnnual}`} />
            <Stat label="Total return" value={`${data.returnsPct}%`} />
            <Stat label="Risk score" value={data.riskScore} />
          </div>

          {/* HOLDINGS */}
          <section>
            <h2 className="mb-2 font-semibold text-ink">My investments</h2>
            <Stagger className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
              {data.holdings.map((h) => (
                <StaggerItem key={h.id}>
                  <Link
                    to={`/marketplace/${h.id}`}
                    className="flex items-center gap-3 px-4 py-3 transition hover:bg-surface-sunken"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">{h.title}</p>
                      <p className="truncate text-xs text-ink-subtle">
                        {h.category} &middot; {h.units} tokens &middot; KES {h.income} earned
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="tabular text-sm font-semibold text-ink">KES {h.value}</p>
                      <p
                        className={`tabular text-xs font-medium ${h.returnPct >= 0 ? 'text-accent' : 'text-danger'}`}
                      >
                        {h.returnPct >= 0 ? '+' : ''}
                        {h.returnPct}%
                      </p>
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          </section>
        </div>

        {/* RAIL: allocation + income */}
        <div className="space-y-6">
          <section className="rounded-card border border-border bg-surface p-5 shadow-card">
            <h2 className="font-semibold text-ink">Allocation</h2>
            <div className="mt-3 flex h-2.5 overflow-hidden rounded-full">
              {data.allocation.map((a) => (
                <div
                  key={a.label}
                  className={`${BAR[a.tone] ?? 'bg-primary'}`}
                  style={{ width: `${a.pct}%` }}
                  title={`${a.label} ${a.pct}%`}
                />
              ))}
            </div>
            <ul className="mt-3 space-y-2">
              {data.allocation.map((a) => (
                <li key={a.label} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-ink-muted">
                    <span className={`h-2.5 w-2.5 rounded-sm ${BAR[a.tone] ?? 'bg-primary'}`} />
                    {a.label}
                  </span>
                  <span className="tabular font-medium text-ink">{a.pct}%</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-card border border-border bg-surface p-5 shadow-card">
            <h2 className="font-semibold text-ink">Recent income</h2>
            <ul className="mt-3 space-y-3">
              {data.income.map((inc, i) => (
                <li key={i} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{inc.kind}</p>
                    <p className="truncate text-xs text-ink-subtle">{inc.source}</p>
                  </div>
                  <span className="tabular shrink-0 text-sm font-semibold text-accent">
                    + KES {inc.amount}
                  </span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              disabled
              title="Available in a later sandbox phase"
              className="mt-4 w-full cursor-not-allowed rounded-field border border-border py-2 text-sm font-medium text-ink-muted opacity-60"
            >
              Withdraw earnings (coming soon)
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}
